import { buildApiRequestHeaders, forwardCookiesToClient, navigateTo, OpenapiError, useApm, useCoreConfig, useCoreLogger, useRequestEvent, useRequestHeaders, useRequestURL } from '#imports'
import type { UseOpenapiOptions } from '../../types'
import type { ClientOptions as OpenapiClientOptions, Client as OpenapiClient, Middleware as OpenapiMiddleware } from 'openapi-fetch'
import createClient from 'openapi-fetch'
import { joinURL } from 'ufo'
import { defu } from 'defu'
import { once } from 'es-toolkit'
import type { NitroFetchOptions } from 'nitropack'

export const useOpenapi = <Paths extends object>(options: UseOpenapiOptions): OpenapiClient<Paths> => {
    const { enableCookieForwarding = true, middleware: userMiddleware, service, ...fetchOptions } = options

    const config = useCoreConfig()
    const event = useRequestEvent()
    const url = useRequestURL()
    const requestHeaders = useRequestHeaders()
    const apm = useApm()
    const getLogger = once(() => useCoreLogger().withTag('useOpenapi'))

    let baseUrl = fetchOptions.baseUrl

    if (import.meta.server) {
        const serviceConfig = service && config.services?.[service]?.server

        if (serviceConfig?.baseUrl) {
            baseUrl = joinURL(serviceConfig.baseUrl, baseUrl || '')
        }
    }

    if (!baseUrl) {
        baseUrl = `${useRequestURL().origin}`
    }

    const preparedOptions = defu<OpenapiClientOptions, OpenapiClientOptions[]>({
        baseUrl,
        credentials: 'include',
    },
    fetchOptions,
    {
        fetch: async (input) => {
            const response = await $fetch.raw(input, {
                ...input,
                method: input.method as NitroFetchOptions<any>['method'] ?? 'GET',
                ignoreResponseError: true,
                responseType: 'stream',
            })

            return response
        }
    })

    const client = createClient<Paths>(preparedOptions)

    // Add middleware
    const middleware: OpenapiMiddleware = {
        onRequest(context) {
            if (import.meta.server) {
                const url: string = context.request.url

                let isOutsideNebula = false

                if (url.startsWith('/')) {
                    if (!baseUrl.includes('cego-nebula-http-proxy')) {
                        isOutsideNebula = true
                    }
                }
                else if (!url.includes('cego-nebula-http-proxy')) {
                    isOutsideNebula = true
                }

                if (isOutsideNebula) {
                    getLogger().warn({
                        message: `Request made outside of nebula. Url is: ${url}. baseURL is: ${baseUrl}`,
                        isOutsideNebula
                    })
                }
            }

            const serviceConfig = service && config.services?.[service]

            const preparedHeaders = buildApiRequestHeaders({
                event: event!,
                requestHeaders,
                extraHeaders: {
                    ...fetchOptions.headers,
                    ...Object.fromEntries(context.request.headers),
                },
                service: serviceConfig
            })

            preparedHeaders.forEach((value, key) => {
                context.request.headers.set(key, value)
            })

            if (userMiddleware?.onRequest) {
                return userMiddleware.onRequest(context)
            }

            return context.request
        },
        async onResponse(context) {
            if (import.meta.server) {
                if (enableCookieForwarding) {
                    forwardCookiesToClient(event!, context.response.headers)
                }
            }

            if (import.meta.client && context.response?.status === 401) {
                await navigateTo({
                    path: config.site?.frontpageUrl || '/',
                    query: {
                        returnUrl: url.pathname,
                    },
                })
            }

            if (!context.response.ok) {
                let _data = undefined

                try {
                    _data = await context.response.clone().json()
                }
                catch {
                    _data = await context.response.clone().text()
                }

                const error = new OpenapiError({
                    ...context,
                    response: Object.assign(context.response, { _data })
                })

                if (import.meta.server) {
                    getLogger().error(error)
                }
                else {
                    apm.captureError(error)
                }

                throw error
            }

            if (userMiddleware?.onResponse) {
                return userMiddleware.onResponse(context)
            }

            return context.response
        },
        onError(context) {
            if (OpenapiError.isError(context?.error)) {
                return
            }

            const error = new OpenapiError(context)

            if (import.meta.server) {
                getLogger().error(error)
            }
            else {
                apm.captureError(error)
            }

            throw error
        }
    }

    client.use(middleware)

    return client
}
